import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { codeEditorApi } from '../../../utils/api/CodeEditorApi';
import useAIFeedback from './useAIFeedback';

const useCodeEditor = (classContext = null) => {
  // Start with sample code to demonstrate AI feedback
  const [code, setCode] = useState(`class ChessGame:
    def __init__(self):
        self.current_player = 'white'

    def move_piece(self, from_pos, to_pos):
        # TODO: Add move validation
        self.board = [[None for _ in range(8)] for _ in range(8)]

    def game_loop(self):
        print("Starting chess game...")

        while True:
            # Game logic here
            pass`);
  const [language, setLanguage] = useState('python');
  const [theme, setTheme] = useState('vs-dark');
  const [activeFile, setActiveFile] = useState(null);
  const [showExplorer, setShowExplorer] = useState(true);

  // Project and file management
  const [projects, setProjects] = useState([]);
  const [currentProject, setCurrentProject] = useState(null);
  const [files, setFiles] = useState({});
  const [standaloneFiles, setStandaloneFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Autosave functionality - use more stable refs
  const autosaveTimer = useRef(null);
  const lastSavedContent = useRef('');
  const lastActiveFileId = useRef(null);
  const [saving, setSaving] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState(null);

  // AI Feedback integration
  const aiFeedback = useAIFeedback(classContext);

  const languages = useMemo(() => [
    { value: 'javascript', label: 'JavaScript', ext: '.js' },
    { value: 'python', label: 'Python', ext: '.py' },
    { value: 'java', label: 'Java', ext: '.java' },
    { value: 'cpp', label: 'C++', ext: '.cpp' },
    { value: 'matlab', label: 'MATLAB', ext: '.m' },
    { value: 'html', label: 'HTML', ext: '.html' },
    { value: 'css', label: 'CSS', ext: '.css' },
    { value: 'sql', label: 'SQL', ext: '.sql' },
    { value: 'json', label: 'JSON', ext: '.json' }
  ], []);

  const themes = [
    { value: 'vs-dark', label: 'Dark' },
    { value: 'light', label: 'Light' },
    { value: 'hc-black', label: 'High Contrast Dark' }
  ];

  // Note: File loading is now handled by useRealtimeCodeEditor hook
  // This hook focuses on editor functionality only

  // Autosave function with HTTP API
  const performAutosave = useCallback(async (content) => {
    if (!activeFile || !classContext?.class_id) {
      // Only show warning if we actually have content to save
      if (content && content.length > 0) {
        console.warn('Cannot autosave: no active file or class context', {
          hasActiveFile: !!activeFile,
          hasClassContext: !!classContext?.class_id
        });
      }
      return;
    }

    // Don't save if content hasn't changed from the last saved version
    if (content === lastSavedContent.current) {
      return;
    }

    try {
      setSaving(true);

      const response = await codeEditorApi.updateFile(
        activeFile.file.file_id,
        content,
        'autosave',
        classContext
      );

      if (response.success) {
        lastSavedContent.current = content;
        setLastSaveTime(new Date());
      } else {
        console.error('❌ Autosave failed:', response.error);
      }
    } catch (error) {
      console.error('❌ Autosave error:', error);
    } finally {
      setSaving(false);
    }
  }, [activeFile, classContext]); // Include activeFile and classContext in dependencies

  // Debounced autosave - saves 2 seconds after user stops typing
  const scheduleAutosave = useCallback((content) => {
    const currentFileId = activeFile?.file?.file_id;

    // Clear existing timer first
    if (autosaveTimer.current) {
      clearTimeout(autosaveTimer.current);
      autosaveTimer.current = null;
    }

    // Don't schedule if no active file or no content
    if (!currentFileId) {
      return;
    }

    // Don't schedule if content is empty or unchanged
    if (!content || content === lastSavedContent.current) {
      return;
    }

    // Schedule new autosave
    const timerId = setTimeout(() => {
      performAutosave(content);
    }, 2000); // 2 second delay

    autosaveTimer.current = timerId;
    lastActiveFileId.current = currentFileId;
  }, [activeFile, performAutosave]);

  // Reset autosave state when switching files (only when file actually changes)
  useEffect(() => {
    const currentFileId = activeFile?.file?.file_id;

    if (currentFileId && currentFileId !== lastActiveFileId.current) {
      lastSavedContent.current = code;
      setLastSaveTime(null);

      // Clear any pending autosave when switching files
      if (autosaveTimer.current) {
        clearTimeout(autosaveTimer.current);
        autosaveTimer.current = null;
      }

      lastActiveFileId.current = currentFileId;
    }
  }, [activeFile?.file?.file_id, code]);

  // Cleanup autosave timer on unmount
  useEffect(() => {
    return () => {
      if (autosaveTimer.current) {
        clearTimeout(autosaveTimer.current);
      }
    };
  }, []);

  // Manual refresh function using HTTP API
  const refreshFiles = useCallback(async () => {
    if (!classContext?.class_id) {
      console.warn('Cannot refresh files: No class context');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await codeEditorApi.getUserProjects(classContext);

      if (response.success) {
        const { projects: userProjects, standalone_files } = response.data;

        setProjects(userProjects);
        setStandaloneFiles(standalone_files);

        // Convert projects and files to the file tree structure
        const fileTree = {};

        // Add standalone files to root
        standalone_files.forEach(file => {
          fileTree[file.file.name] = {
            content: file.content.current,
            language: file.file.syntax_language,
            type: 'file',
            fileId: file.file.file_id,
            projectId: null
          };
        });

        // Add projects as expandable folders
        userProjects.forEach(project => {
          const projectKey = project.name;
          fileTree[projectKey] = {
            type: 'project',
            expanded: false,
            projectId: project.project_id,
            runtimeLanguage: project.runtime_language,
            description: project.description,
            children: {}
          };

          // Add files to project
          if (project.files && project.files.length > 0) {
            project.files.forEach(file => {
              fileTree[projectKey].children[file.file.name] = {
                content: file.content.current,
                language: file.file.syntax_language,
                type: 'file',
                fileId: file.file.file_id,
                projectId: project.project_id
              };
            });
          }
        });

        setFiles(fileTree);
      }
    } catch (err) {
      setError(err.message);
      console.error('Failed to load projects and files:', err);
    } finally {
      setLoading(false);
    }
  }, [classContext]);

  const handleEditorChange = useCallback((value) => {
    setCode(value);

    // Schedule autosave (debounced) - scheduleAutosave handles the activeFile check
    scheduleAutosave(value);

    // Trigger AI feedback analysis on code changes
    if (activeFile) {
      aiFeedback.analyzeCodeChange(value, language, activeFile);
    }

    // Update the active file's content in the file tree
    if (activeFile) {
      const fileName = activeFile?.file?.name || activeFile?.name;
      if (!fileName) {
        console.warn('No file name found for activeFile:', activeFile);
        return;
      }

      setFiles(prev => {
        const updateFileContent = (fileStructure, path) => {
          const pathParts = path.split('/');
          const targetFileName = pathParts[pathParts.length - 1];

          if (pathParts.length === 1) {
            return {
              ...fileStructure,
              [targetFileName]: {
                ...fileStructure[targetFileName],
                content: value
              }
            };
          }

          const folderName = pathParts[0];
          const remainingPath = pathParts.slice(1).join('/');

          return {
            ...fileStructure,
            [folderName]: {
              ...fileStructure[folderName],
              children: updateFileContent(fileStructure[folderName].children, remainingPath)
            }
          };
        };

        return updateFileContent(prev, fileName);
      });
    }
  }, [activeFile, scheduleAutosave, aiFeedback, language]);

  const handleLanguageChange = useCallback((newLanguage) => {
    setLanguage(newLanguage);
  }, []);

  const openFile = useCallback((filePathOrItem, fileObj) => {
    // Handle both old interface (filePath, fileObj) and new interface (fileItem only)
    let fileItem;

    if (fileObj) {
      // Old interface: openFile(filePath, fileObj)
      fileItem = fileObj;
    } else {
      // New interface: openFile(fileItem)
      fileItem = filePathOrItem;
    }

    setActiveFile(fileItem);

    // Clear existing feedback when opening a new file
    aiFeedback.clearFeedback();

    // Handle different data structures and ensure content is a string
    let content = '';

    if (fileItem?.content?.current !== undefined) {
      // New format: content is an object with current field
      content = fileItem.content.current;
    } else if (fileItem?.content !== undefined) {
      // Old format: content is direct
      content = fileItem.content;
    }

    // Ensure content is always a string
    if (typeof content !== 'string') {
      content = content ? String(content) : '';
    }

    console.log('Language detection debug:', {
      syntax_language: fileItem?.file?.syntax_language,
      fileItem_language: fileItem?.language,
      file_name: fileItem?.file?.file_name,
      filePathOrItem: filePathOrItem,
      activeFileName: activeFile?.file?.file_name
    });

    // Deep inspection of the file object structure
    if (filePathOrItem && typeof filePathOrItem === 'object') {
      console.log('filePathOrItem structure:', {
        keys: Object.keys(filePathOrItem),
        file: filePathOrItem.file,
        fileKeys: filePathOrItem.file ? Object.keys(filePathOrItem.file) : null
      });
    }

    // Try to get filename from various sources
    let fileName = null;

    if (typeof filePathOrItem === 'string') {
      fileName = filePathOrItem;
    } else if (filePathOrItem?.file?.name) {
      fileName = filePathOrItem.file.name;
    } else if (filePathOrItem?.file?.file_name) {
      fileName = filePathOrItem.file.file_name;
    } else if (fileItem?.file?.name) {
      fileName = fileItem.file.name;
    } else if (fileItem?.file?.file_name) {
      fileName = fileItem.file.file_name;
    } else if (fileItem?.fileName) {
      fileName = fileItem.fileName;
    }

    console.log('Filename detection:', { fileName });

    // Always check file extension first for MATLAB files
    let language = null;
    let ext = null;

    // Get extension from filename or directly from the file object
    if (fileName && typeof fileName === 'string') {
      ext = fileName.split('.').pop();
    } else if (filePathOrItem?.file?.extension) {
      ext = filePathOrItem.file.extension.replace('.', ''); // Remove leading dot if present
    }

    if (ext) {
      const detectedLang = languages.find(l => l.ext === `.${ext}`)?.value;

      console.log('File extension detection:', {
        fileName,
        ext,
        detectedLang,
        availableLanguages: languages.map(l => ({ value: l.value, ext: l.ext }))
      });

      // For MATLAB files, always use matlab language regardless of server setting
      if (ext === 'm' && detectedLang === 'matlab') {
        language = 'matlab';
        console.log('Detected MATLAB file - forcing matlab language');
      } else {
        // For other files, prefer server language if available
        language = fileItem?.file?.syntax_language || fileItem?.language || detectedLang;
      }
    }

    // Fall back to server language or javascript
    language = language || fileItem?.file?.syntax_language || fileItem?.language || 'javascript';

    console.log('Final language determined:', language);

    setCode(content);
    setLanguage(language);

    // Reset the last saved content to match the file content when opening
    lastSavedContent.current = content;

    // Trigger AI feedback analysis for the opened file
    if (content && content.trim().length > 0) {
      setTimeout(() => {
        aiFeedback.refreshFeedback(content, language, fileItem);
      }, 500); // Small delay to ensure editor is ready
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languages, aiFeedback]);

  const toggleFolder = useCallback((folderPath) => {
    setFiles(prev => {
      const updateFolder = (fileStructure, path) => {
        const pathParts = path.split('/');
        const folderName = pathParts[0];

        if (pathParts.length === 1) {
          return {
            ...fileStructure,
            [folderName]: {
              ...fileStructure[folderName],
              expanded: !fileStructure[folderName].expanded
            }
          };
        }

        const remainingPath = pathParts.slice(1).join('/');
        return {
          ...fileStructure,
          [folderName]: {
            ...fileStructure[folderName],
            children: updateFolder(fileStructure[folderName].children, remainingPath)
          }
        };
      };

      return updateFolder(prev, folderPath);
    });
  }, []);

  // Helper function to find file in tree structure
  const findFileInTree = useCallback((fileTree, fileName) => {
    for (const [key, value] of Object.entries(fileTree)) {
      if (key === fileName && value.type === 'file') {
        return value;
      }
      if (value.type === 'folder' && value.children) {
        const found = findFileInTree(value.children, fileName);
        if (found) return found;
      }
    }
    return null;
  }, []);

  const createNewFile = useCallback(async (projectId = null, fileName = null) => {
    const fileNameToUse = fileName || prompt('Enter file name:');
    if (!fileNameToUse) return;

    // Check if file already exists
    const existingFile = findFileInTree(files, fileNameToUse);
    if (existingFile) {
      alert('File already exists!');
      return;
    }

    setLoading(true);
    try {
      const response = await codeEditorApi.createFile(fileNameToUse, '', projectId, classContext);

      if (response.success) {
        const newFile = response.file;
        await refreshFiles(); // Reload to get updated structure

        // Open the newly created file
        const ext = fileNameToUse.split('.').pop();
        const lang = languages.find(l => l.ext === `.${ext}`)?.value || 'javascript';
        openFile(fileNameToUse, {
          content: '',
          language: lang,
          fileId: newFile.file.file_id,
          projectId: projectId
        });
        return true;
      } else {
        setError(response.error);
        return false;
      }
    } catch (err) {
      setError(err.message);
      console.error('Failed to create file:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [files, languages, refreshFiles, findFileInTree, openFile, classContext]);

  const handleSave = useCallback(async () => {
    if (!activeFile || !classContext?.class_id) {
      console.warn('No active file or class context for saving');
      return;
    }

    // Handle both old and new activeFile formats
    let fileObj = null;
    let fileId = null;

    if (typeof activeFile === 'string') {
      // Old format: activeFile is a file path string
      fileObj = findFileInTree(files, activeFile) ||
                standaloneFiles.find(f => f.file.name === activeFile);
      fileId = fileObj?.file?.file_id || fileObj?.fileId;
    } else if (activeFile && typeof activeFile === 'object') {
      // New format: activeFile is the file object itself
      fileObj = activeFile;
      fileId = activeFile?.file?.file_id || activeFile?.fileId;
    }

    if (!fileObj || !fileId) {
      console.error('Could not find file ID for saving', { fileObj, activeFile });
      return;
    }

    setLoading(true);
    try {
      const response = await codeEditorApi.updateFile(
        fileId,
        code,
        'manual_edit',
        classContext
      );

      if (response.success) {
        console.log('File saved successfully:', activeFile);
        // Update local state to reflect the save
        await refreshFiles();
      } else {
        setError(response.error);
      }
    } catch (err) {
      setError(err.message);
      console.error('Failed to save file:', err);
    } finally {
      setLoading(false);
    }
  }, [activeFile, code, classContext, files, standaloneFiles, findFileInTree, refreshFiles]);

  // Project management functions
  const createProject = useCallback(async (projectName, runtimeLanguage, description = '') => {
    if (!classContext?.class_id) {
      setError('No class selected. Please select a class first.');
      return null;
    }

    setLoading(true);
    try {
      const response = await codeEditorApi.createProject(
        projectName,
        runtimeLanguage,
        description,
        classContext
      );

      if (response.success) {
        await refreshFiles(); // Reload to get updated projects
        return response.project;
      } else {
        setError(response.error);
        return null;
      }
    } catch (err) {
      setError(err.message);
      console.error('Failed to create project:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [classContext, refreshFiles]);

  const switchProject = useCallback((project) => {
    setCurrentProject(project);
    // Clear active file when switching projects
    setActiveFile(null);
    setCode('');
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Handle AI feedback clicks
  const handleFeedbackClick = useCallback((feedbackItem) => {
    // Show feedback pane if not already visible
    if (!aiFeedback.showFeedback) {
      aiFeedback.setShowFeedback(true);
    }

    // Could add more interaction logic here
    console.log('Feedback clicked:', feedbackItem);
  }, [aiFeedback]);

  // Manual AI feedback refresh
  const refreshAIFeedback = useCallback(() => {
    if (activeFile && code) {
      aiFeedback.refreshFeedback(code, language, activeFile);
    }
  }, [activeFile, code, language, aiFeedback]);

  return {
    // State
    code,
    language,
    theme,
    activeFile,
    showExplorer,
    files,
    projects,
    currentProject,
    standaloneFiles,
    loading,
    error,
    languages,
    themes,

    // Autosave state
    saving,
    lastSaveTime,

    // AI Feedback state
    aiFeedback: {
      items: aiFeedback.feedbackItems,
      loading: aiFeedback.loading,
      error: aiFeedback.error,
      showFeedback: aiFeedback.showFeedback,
      hasFeedback: aiFeedback.hasFeedback
    },

    // Actions
    setCode,
    setLanguage,
    setTheme,
    setActiveFile,
    setShowExplorer,
    handleEditorChange,
    handleLanguageChange,
    openFile,
    toggleFolder,
    createNewFile,
    handleSave,
    createProject,
    switchProject,
    refreshFiles,
    clearError,

    // AI Feedback actions
    handleFeedbackClick,
    refreshAIFeedback,
    toggleAIFeedback: aiFeedback.toggleFeedback,
    setShowAIFeedback: aiFeedback.setShowFeedback
  };
};

export default useCodeEditor;